import React, { FC, useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { Loader, SdirModal, TextLink } from '@sdir/sds';
import { useNumberService } from '@sdir/utilities/lib/hooks';
import {
  ReportSeafarerSeagoingTimeInDays,
  Detail,
  SeagoingDetails,
} from '@sdir/httpclient/lib/clients/aps/seagoing';
import styled from 'styled-components';
import { useApi } from '@sdir/httpclient/lib/hooks/useApi';
import { ErrorMessage } from '@sdir/utilities/lib/components';
import { DisplayDateFormat } from '../../../helpers';
import { seagoingTimeCalculatorApi } from '../../../httpclient';

interface SeagoingLogInfoProps {
  report: ReportSeafarerSeagoingTimeInDays;
  detail: Detail;
}

export const SeagoingLogInfo: FC<SeagoingLogInfoProps> = ({ report, detail }) => {
  const intl = useIntl();
  const numberService = useNumberService();
  const [isOpen, setIsOpen] = useState(false);

  const { result, loading, errorObject, callApi } = useApi({
    apiFunction: () =>
      // eslint-disable-next-line max-len
      seagoingTimeCalculatorApi.v1SeagoingtimecalculatorGetownseagoingdetailsOwnSeagoingDetailsQueryPost(
        {
          callSign: detail.vessel?.callSign,
          imoNumber: detail.vessel?.imoNumber,
          name: detail.vessel?.name,
          serviceCode: report.serviceCode,
          startDate: detail.signOnDate || undefined,
          endDate: detail.signOffDate || undefined,
          organizationNumber: detail.organizationNumber,
        }
      ),
    callApiOnInit: false,
  });

  const handleLinkClick = useCallback(() => {
    setIsOpen(true);
    callApi();
  }, [callApi]);
  const handleCancel = useCallback(() => setIsOpen(false), []);

  return (
    <>
      <TextLink
        text={intl.formatMessage({ id: 'general.table.viewdetails' })}
        onClick={handleLinkClick}
      />
      <StyledModal visible={isOpen} onCancel={handleCancel} closeButton={false}>
        {loading && <Loader />}
        {errorObject && <ErrorMessage error={errorObject} />}
        {result?.details?.map((item) => {
          return (
            <LogEntry
              daysValue={detail?.seagoingDays || 0}
              fm={intl.formatMessage}
              ns={numberService}
              rowData={item}
            />
          );
        })}
      </StyledModal>
    </>
  );
};

const StyledModal = styled(SdirModal)`
  &&& {
    > * {
      min-width: 50em;
      padding: 3rem 4rem;
    }

    table {
      max-width: calc(100vw - 14rem);
      width: 118rem;

      th {
        white-space: nowrap;
      }
    }
  }

  @media screen and (max-width: ${({ theme }) => theme.screen.large}) {
    &&& {
      > * {
        min-width: 22em;
      }
    }
  }
`;

interface LogEntryProps {
  rowData: SeagoingDetails;
  fm: any;
  ns: any;
  daysValue: number;
}

const LogEntry: React.FC<LogEntryProps> = ({ rowData, fm, ns, daysValue }) => {
  return (
    <LogEntryWrapper>
      {/*       <LogEntrySubItem>
        <SubHeader>{fm({ id: 'seagoing.loginfo.table.reportedby' })}</SubHeader>
        <SubValue>{rowData.createdBy || ''}</SubValue>
      </LogEntrySubItem> */}
      <LogEntrySubItem>
        <SubHeader>{fm({ id: 'seagoing.loginfo.table.date' })}</SubHeader>
        <SubValue>{rowData.createDate ? DisplayDateFormat(rowData.createDate) : ''}</SubValue>
      </LogEntrySubItem>
      <LogEntrySubItem>
        <SubHeader>{fm({ id: 'seagoing.table.childtable.period' })}</SubHeader>
        <SubValue>
          {`${rowData.period?.signOnDate ? DisplayDateFormat(rowData.period.signOnDate) : ''} - ${
            rowData.period?.signOffDate ? DisplayDateFormat(rowData.period.signOffDate) : ''
          }`}
        </SubValue>
      </LogEntrySubItem>
      <LogEntrySubItem>
        <SubHeader>{fm({ id: 'seagoing.table.childtable.employer' })}</SubHeader>
        <SubValue>{rowData.organizationName || ''}</SubValue>
      </LogEntrySubItem>
      <LogEntrySubItem>
        <SubHeader>{fm({ id: 'seagoing.table.totaltimedays' })}</SubHeader>
        <SubValue>{ns.formatDecimal(daysValue)}</SubValue>
      </LogEntrySubItem>
    </LogEntryWrapper>
  );
};

const LogEntryWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-bottom: 50px;
`;

const LogEntrySubItem = styled.div`
  margin-bottom: 10px;
  margin-right: 15px;
  width: calc(25% - 15px);

  @media screen and (max-width: ${({ theme }) => theme.screen.large}) {
    width: calc(50% - 15px);
  }

  @media screen and (max-width: ${({ theme }) => theme.screen.medium}) {
    width: 100%;
    margin-right: 0;
  }
`;

const SubHeader = styled.span`
  display: block;
  font-weight: bold;
  margin-bottom: 2px;
`;

const SubValue = styled.span`
  display: block;
`;
